import React, { useState, useEffect, useCallback } from 'react';
import { Button, Modal, Form, Input, InputNumber, Spin, Alert } from 'antd';
import { DataGrid } from '@mui/x-data-grid';
import { httpGet, httpPut, customBaseUrl } from '../../store/http';
import { NotificationManager } from 'react-notifications';

const TierLimitsSettings = () => {
  const [tierLimits, setTierLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchTierLimits = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpGet(`${customBaseUrl.authUrl}/api/v1/admin/users/tier-limits/all`);
      if (res?.data) {
        const limitsWithId = res.data.map(item => ({ ...item, id: item.tierName }));
        setTierLimits(limitsWithId);
      } else {
        setError(res?.message || 'Failed to fetch tier limits.');
        NotificationManager.error(res?.message || 'Failed to fetch tier limits.');
      }
    } catch (err) {
      setError('An error occurred while fetching tier limits.');
      NotificationManager.error('An error occurred while fetching tier limits.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTierLimits();
  }, [fetchTierLimits]);

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleUpdate = async (values) => {
    if (!editingRecord) return;

    const payload = {
      tierName: editingRecord.tierName,
      dailyTransactionLimit: values.dailyTransactionLimit,
      oneTimeTransferLimit: values.oneTimeTransferLimit,
      description: values.description,
    };

    try {
      const res = await httpPut(`${customBaseUrl.authUrl}/api/v1/admin/users/tier-limits/update`, payload);
      if (res?.success) {
        NotificationManager.success('Tier limit updated successfully.');
        handleCancel();
        fetchTierLimits();
      } else {
        NotificationManager.error(res?.message || 'Failed to update tier limit.');
      }
    } catch (err) {
      NotificationManager.error('An error occurred while updating tier limit.');
      console.error(err);
    }
  };

  const columns = [
    {
      field: 'tierName',
      headerName: 'Tier Name',
      width: 150,
    },
    {
      field: 'dailyTransactionLimit',
      headerName: 'Daily Transaction Limit',
      width: 200,
      type: 'number',
    },
    {
      field: 'oneTimeTransferLimit',
      headerName: 'One-Time Transfer Limit',
      width: 200,
      type: 'number',
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 150,
      renderCell: (params) => (
        <Button type="primary" onClick={() => handleEdit(params.row)}>
          Edit
        </Button>
      ),
    },
  ];

  if (loading) {
    return <Spin tip="Loading Tier Limits..." />;
  }

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  return (
    <div style={{ height: 600, width: '100%' }}>
      <DataGrid
        rows={tierLimits}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10]}
        disableSelectionOnClick
      />
      <Modal
        title="Edit Tier Limit"
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        okText="Update"
      >
        <Form form={form} layout="vertical" onFinish={handleUpdate} initialValues={editingRecord}>
          <Form.Item label="Tier Name">
            <Input value={editingRecord?.tierName} disabled />
          </Form.Item>
          <Form.Item
            name="dailyTransactionLimit"
            label="Daily Transaction Limit"
            rules={[{ required: true, message: 'Please input the daily transaction limit!' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="oneTimeTransferLimit"
            label="One-Time Transfer Limit"
            rules={[{ required: true, message: 'Please input the one-time transfer limit!' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please input the description!' }]}
          >
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TierLimitsSettings;
